import React, { useState, useEffect, useMemo } from 'react';

import { useSessionStatus } from '../queries/session';
import SessionExpirationBanner from './sessionWatcherBanner.component';
import { useRefreshSession } from '../mutations/session';
import { redirectToAuthorize } from '../../utils/fetchService';
import {
  SESSION_EXPIRE_NORMAL_REFRESH_TIME,
  SESSION_EXPIRE_WARNING_REFRESH_TIME,
  SESSION_EXPIRE_WARNING_TIME,
} from '../constants';

const getTimeout = (inactivity?: number | null, session?: number | null) => {
  if (!inactivity && !session) {
    return { isSessionTimingOut: false, timeDistance: null };
  }
  if (!inactivity) {
    return { isSessionTimingOut: true, timeDistance: session };
  }
  if (!session) {
    return { isSessionTimingOut: false, timeDistance: inactivity };
  }
  if (session > inactivity) {
    return { isSessionTimingOut: false, timeDistance: inactivity };
  }

  return { isSessionTimingOut: true, timeDistance: session };
};

export const SessionWatcher = () => {
  const [counter, setCounter] = useState<number | null>(null);
  const { data: { inactivity, session } } = useSessionStatus({
    refetchInterval: counter && counter < SESSION_EXPIRE_WARNING_TIME
      ? SESSION_EXPIRE_WARNING_REFRESH_TIME
      : SESSION_EXPIRE_NORMAL_REFRESH_TIME,
  });
  const { mutate: refreshSession, isLoading } = useRefreshSession();

  const { isSessionTimingOut, timeDistance } = getTimeout(inactivity, session);

  const expirationDate = useMemo(() => timeDistance ? Date.now() / 1000 + timeDistance : null, [timeDistance]);
  useEffect(() => {
    const intervalId = setInterval(() => setCounter(expirationDate && (expirationDate - Date.now() / 1000)), 1000);
    return () => clearInterval(intervalId);
  }, [expirationDate]);

  if (!counter || counter > SESSION_EXPIRE_WARNING_TIME) {
    return null;
  }

  return (
    <SessionExpirationBanner
      timeDistance={counter}
      refreshSession={isSessionTimingOut ? redirectToAuthorize : refreshSession}
      disableRefresh={isLoading}
    />
  );
};
